import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import type { ProposalNodeSet, TimberSupplierStructureRef } from '../types';
import { db } from './firebase';
import { enabledProposalStructures } from './proposalStructureRefs';
import { rfqStructureNodeSets } from './rfqProjectStructures';
import { normalizeQuotedStructures } from './timberQuoteStreamKey';

/**
 * Enabled proposal structures for a deal (quote structure pickers / CNC payload).
 *
 * Pass the Project Hub id so structures no Files track is tagged to — typically the
 * proposal's pre-split whole-project default — drop out (see rfqProjectStructures).
 * Staff-only: reading `projectFileStreams` needs CRM access, so supplier-portal callers
 * omit it and get every enabled structure.
 */
export async function loadDealProposalStructures(
  dealId: string | null | undefined,
  options?: { hubProjectId?: string | null },
): Promise<TimberSupplierStructureRef[]> {
  const id = (dealId || '').trim();
  if (!id) return [];
  try {
    const snap = await getDoc(doc(db, 'proposalBuilders', id));
    if (!snap.exists()) return [];
    const nodeSets = (snap.data() as { nodeSets?: ProposalNodeSet[] }).nodeSets;
    const hubProjectId = (options?.hubProjectId || '').trim();
    if (!hubProjectId) return normalizeQuotedStructures(enabledProposalStructures(nodeSets));
    let streams: { structureIds?: string[] }[] = [];
    try {
      const streamSnap = await getDocs(query(collection(db, 'projectFileStreams'), where('projectId', '==', hubProjectId)));
      streams = streamSnap.docs.map(d => ({ structureIds: (d.data().structureIds as string[] | undefined) ?? [] }));
    } catch {
      // No track access — fall back to every enabled structure.
    }
    const quotable = rfqStructureNodeSets(nodeSets, streams);
    return normalizeQuotedStructures(enabledProposalStructures(quotable));
  } catch {
    return [];
  }
}
